import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useGetBusiness } from '@marketing-milk/frontend'
import {
  NarrativeBlock as NarrativeBlockData,
  NarrativeSectionKey,
  SprintPacketHydrated,
} from '@marketing-milk/interfaces'
import { useMentions } from 'hooks/useMentions'
import { SlackUser } from 'hooks/useSlackUsers'
import { useUpdateSprintPacket } from 'hooks/api/useSprintPackets'

// Hoisted, not an inline `[]`: `useMentions` filters on this by reference, so a
// fresh array each render re-runs that effect, which sets state, which renders
// again — an unbroken loop that hangs the page.
const NO_MENTIONABLE_USERS: SlackUser[] = []

export const useNarrativeBlockSave = (
  packetID: number,
  sectionKey: NarrativeSectionKey,
  narrative: NarrativeBlockData[]
) => {
  const block = narrative.find(b => b.sectionKey === sectionKey)
  const hydratedText = block?.richText ?? ''

  const [text, setText] = useState(hydratedText)
  const [cursorPos, setCursorPos] = useState(0)
  const [controlledCursorPos, setControlledCursorPos] = useState<number | undefined>(undefined)
  const lastSavedRef = useRef(hydratedText)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (hydratedText !== lastSavedRef.current && text === lastSavedRef.current) {
      setText(hydratedText)
    }
    lastSavedRef.current = hydratedText
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydratedText])

  useEffect(() => {
    if (controlledCursorPos !== undefined) {
      const t = setTimeout(() => setControlledCursorPos(undefined), 50)
      return () => clearTimeout(t)
    }
  }, [controlledCursorPos])

  // Mentions are intentionally disabled in narrative + budget editors: they
  // can't trigger a Slack notification (no "send" affordance), so an `@Sarah`
  // in prose would silently fail to notify Sarah. Comments are the
  // authorized "ping someone" path — they attach to an anchor and DM on send.
  const mentionState = useMentions(text, cursorPos, NO_MENTIONABLE_USERS)

  const update = useUpdateSprintPacket(packetID)
  const queryClient = useQueryClient()
  const business = useGetBusiness()
  const packetQueryKey = ['sprint-packet', business.id, packetID]

  const save = (value: string) => {
    if (value === lastSavedRef.current) return
    const block = { sectionKey, richText: value, updatedAt: new Date().toISOString() }
    const cached = queryClient.getQueryData<SprintPacketHydrated>(packetQueryKey)
    if (cached) {
      const sourceNarrative = cached.narrative ?? narrative
      queryClient.setQueryData<SprintPacketHydrated>(packetQueryKey, {
        ...cached,
        narrative: [...sourceNarrative.filter(b => b.sectionKey !== sectionKey), block],
      })
    }
    // Send only the changed block; server merges by sectionKey under a row
    // lock so concurrent autosaves to different sections can't clobber each
    // other.
    update.mutate(
      { narrativeBlock: block },
      {
        onSuccess: () => {
          lastSavedRef.current = value
        },
        onError: () => {
          queryClient.invalidateQueries(packetQueryKey)
        },
      }
    )
  }

  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return
    const handler = (e: FocusEvent) => {
      if (!el.contains(e.relatedTarget as Node | null)) {
        save(text)
      }
    }
    el.addEventListener('focusout', handler)
    return () => el.removeEventListener('focusout', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, narrative])

  const indicator = update.isLoading
    ? 'Saving…'
    : update.isError
    ? 'Save failed'
    : text !== lastSavedRef.current
    ? 'Unsaved'
    : block
    ? 'Saved'
    : ''

  return {
    text,
    setText,
    setCursorPos,
    controlledCursorPos,
    setControlledCursorPos,
    mentionState,
    indicator,
    wrapperRef,
  }
}
